/* eslint-disable react/no-unstable-nested-components */
import {
    Badge,
    Button,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    SearchInput,
    Table,
} from "@jhool-io/fe-components";
import * as React from "react";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { IClaim, ClaimStatus } from "../../../claims/types/claims.types";
import {
    BillStatus,
    BillStatusLabels,
} from "../../../billing/types/billing.types";
import {
    formatDate,
    makeStringFirstLetterCapital,
    showMoneyInAppFormat,
} from "../../../../utils/helpers";
import { useDebounce } from "../../../../hooks/helpers";
import { useFetchClaims } from "../../../claims/hooks/claims.queries";
import ListState from "../../../../components/ListState/ListState";
import { useAddElectronicRemitClaims } from "../../hooks/remits.mutations";
import useToast from "../../../../hooks/useToast";
import { IClaimsReport, IUnmatchedRemitLine } from "../../types/remits.types";
import Loader from "../../../../shared-ui/Loader/Loader";
import TableFiltersDisplay from "../../../../shared-ui/TableFiltersDisplay/TableFiltersDisplay";
import { getBillStatusFilterLabel } from "../../../billing/helpers/billing.helpers";
import { getClaimStatusFilterLabel } from "../../../claims/helpers/claims.helper";

interface UnmatchedRemitClaimsProps {
    unmatchedRemits: IUnmatchedRemitLine[];
    batchInsuranceProviderId?: string;
}

interface IUnmatchedRemitGroup {
    ref: string | null;
    patientName: string | null;
    cptCodes: string[];
    paidAmount: number;
}

export default function UnmatchedRemitClaims({
    unmatchedRemits,
    batchInsuranceProviderId,
}: UnmatchedRemitClaimsProps) {
    const params = useParams();
    const remitId = params.remitId || "";

    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedRemitRef, setSelectedRemitRef] = React.useState<
        string | null
    >(null);
    const [searchString, setSearchString] = React.useState("");

    const billStatusFilter = searchParams.getAll("invoice_statuses[]") || [];
    const claimStatusFilter = searchParams.getAll("claim_statuses[]") || [];

    const queryClient = useQueryClient();
    const { toast } = useToast();
    const addElectronicRemitClaims = useAddElectronicRemitClaims(remitId);
    const searchStringDebouncedValue = useDebounce(searchString, 500);

    const remitGroups: IUnmatchedRemitGroup[] = React.useMemo(() => {
        const byRef = new Map<string, IUnmatchedRemitLine[]>();
        const missingRefLines: IUnmatchedRemitLine[] = [];
        unmatchedRemits.forEach((line) => {
            if (!line.claim_to_match) {
                missingRefLines.push(line);
                return;
            }
            const lines = byRef.get(line.claim_to_match) ?? [];
            lines.push(line);
            byRef.set(line.claim_to_match, lines);
        });
        const matchableGroups = Array.from(byRef.entries()).map(
            ([ref, lines]) => ({
                ref,
                patientName: lines[0].patient_name,
                cptCodes: Array.from(
                    new Set(
                        lines
                            .map((line) => line.cpt_code)
                            .filter((cpt): cpt is string => Boolean(cpt))
                    )
                ),
                paidAmount: lines.reduce(
                    (sum, line) => sum + (line.paid_amount || 0),
                    0
                ),
            })
        );
        const missingRefGroups: IUnmatchedRemitGroup[] = missingRefLines.map(
            (line) => ({
                ref: null,
                patientName: line.patient_name,
                cptCodes: line.cpt_code ? [line.cpt_code] : [],
                paidAmount: line.paid_amount || 0,
            })
        );
        return [...matchableGroups, ...missingRefGroups];
    }, [unmatchedRemits]);

    const selectedRemitGroup = selectedRemitRef
        ? remitGroups.find((group) => group.ref === selectedRemitRef)
        : undefined;

    const { data, isFetching, isSuccess, error } = useFetchClaims(
        {
            search_string: searchStringDebouncedValue,
            include_invoice_status: true,
            invoice_statuses:
                billStatusFilter.length > 0 ? billStatusFilter : undefined,
            claim_statuses:
                claimStatusFilter.length > 0 ? claimStatusFilter : undefined,
        },
        Boolean(selectedRemitRef && searchStringDebouncedValue)
    );

    const rankedClaims = React.useMemo(() => {
        const score = (claim: IClaim) => {
            let value = 0;
            if (
                batchInsuranceProviderId &&
                claim.insurance_provider_id === batchInsuranceProviderId
            ) {
                value += 2;
            }
            if (selectedRemitGroup?.cptCodes.includes(claim.cpt_code)) {
                value += 1;
            }
            return value;
        };
        return [...(data?.data || [])]
            .map((claim) => ({ claim, score: score(claim) }))
            .sort((a, b) => b.score - a.score)
            .map((ranked) => ranked.claim);
    }, [data?.data, batchInsuranceProviderId, selectedRemitGroup]);

    const isSuggestedClaim = (claim: IClaim) =>
        Boolean(
            batchInsuranceProviderId &&
                claim.insurance_provider_id === batchInsuranceProviderId &&
                selectedRemitGroup?.cptCodes.includes(claim.cpt_code)
        );

    const buildResultMessage = (
        addedClaims: string[],
        unaddedClaims: IClaimsReport[]
    ) => {
        const parts: string[] = [];
        if (addedClaims.length > 0) {
            parts.push(`${addedClaims.length} claim(s) matched`);
        }
        if (unaddedClaims.length > 0) {
            const reasons = Array.from(
                new Set(unaddedClaims.map((claim) => claim.reason))
            ).join("; ");
            parts.push(
                `${unaddedClaims.length} claim(s) could not be matched: ${reasons}`
            );
        }
        return parts.join(". ");
    };

    const handleOpenClaimPicker = (row: Row<IUnmatchedRemitGroup>) => {
        if (!row.original.ref) return;
        setSelectedRemitRef(row.original.ref);
        setSearchString(row.original.patientName ?? "");
        searchParams.delete("invoice_statuses[]");
        searchParams.delete("claim_statuses[]");
        Object.values(ClaimStatus).forEach((status) => {
            if (status !== ClaimStatus.CLOSED) {
                searchParams.append("claim_statuses[]", status);
            }
        });
        setSearchParams(searchParams);
    };

    const handleCloseClaimPicker = () => {
        setSelectedRemitRef(null);
        setSearchString("");
        searchParams.delete("invoice_statuses[]");
        searchParams.delete("claim_statuses[]");
        setSearchParams(searchParams);
    };

    const handleBillStatusChange = (status: string) => {
        if (billStatusFilter.includes(status)) {
            searchParams.delete("invoice_statuses[]", status);
        } else {
            searchParams.append("invoice_statuses[]", status);
        }
        setSearchParams(searchParams);
    };

    const handleClaimStatusChange = (status: string) => {
        if (claimStatusFilter.includes(status)) {
            searchParams.delete("claim_statuses[]", status);
        } else {
            searchParams.append("claim_statuses[]", status);
        }
        setSearchParams(searchParams);
    };

    const handleMatchClaim = (claim: IClaim) => {
        if (!selectedRemitRef) return;
        addElectronicRemitClaims.mutate(
            [{ claim_id: claim.claim_id, claim_to_match: selectedRemitRef }],
            {
                onSuccess: (res) => {
                    queryClient.invalidateQueries({
                        queryKey: ["remit-claims"],
                        refetchType: "all",
                    });
                    queryClient.invalidateQueries({
                        queryKey: ["batch-remits"],
                        refetchType: "all",
                    });
                    const {
                        added_claims: addedClaims,
                        unadded_claims: unaddedClaims,
                    } = res.data;
                    toast({
                        mode: addedClaims.length > 0 ? "success" : "error",
                        message: buildResultMessage(addedClaims, unaddedClaims),
                    });
                    if (addedClaims.length > 0) {
                        handleCloseClaimPicker();
                    }
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not match claim to remit.",
                    });
                },
            }
        );
    };

    const unmatchedColumns: ColumnDef<IUnmatchedRemitGroup>[] = [
        {
            accessorKey: "patientName",
            header: "PATIENT",
            cell: ({ row }) => row.original.patientName || "--",
        },
        {
            accessorKey: "cptCodes",
            header: "CPT CODE",
            cell: ({ row }) => row.original.cptCodes.join(", ") || "--",
        },
        {
            accessorKey: "paidAmount",
            header: "AMOUNT PAID",
            cell: ({ row }) => showMoneyInAppFormat(row.original.paidAmount),
        },
        {
            accessorKey: "ref",
            header: "REMIT REFERENCE",
            cell: ({ row }) =>
                row.original.ref ?? (
                    <Badge type="warning" content="Missing remit reference" />
                ),
        },
    ];

    const claimColumns: ColumnDef<IClaim>[] = [
        {
            accessorKey: "client_name",
            header: "CLIENT",
            cell: ({ row }) => (
                <div className="flex items-center gap-2">
                    <span>{row.original.client_name}</span>
                    {isSuggestedClaim(row.original) && (
                        <Badge type="info" content="Suggested" />
                    )}
                </div>
            ),
        },
        {
            accessorKey: "date_of_service",
            header: "DATE OF SERVICE",
            cell: ({ row }) => formatDate(row.original.date_of_service),
        },
        {
            accessorKey: "cpt_code",
            header: "CPT CODE",
        },
        {
            accessorKey: "external_reference_id",
            header: "CLAIM ID",
            cell: ({ row }) => row.original.external_reference_id ?? "--",
        },
        {
            accessorKey: "charge_amount",
            header: "AMOUNT",
            cell: ({ row }) => showMoneyInAppFormat(row.original.charge_amount),
        },
        {
            accessorKey: "insurance_name",
            header: "INSURANCE",
            cell: ({ row }) => (
                <span className="uppercase">
                    {makeStringFirstLetterCapital(row.original.insurance_name)}
                </span>
            ),
        },
        {
            id: "match",
            header: "",
            cell: ({ row }) => (
                <Button
                    onClick={() => handleMatchClaim(row.original)}
                    disabled={addElectronicRemitClaims.isLoading}
                >
                    Match
                </Button>
            ),
        },
    ];

    return (
        <>
            <Dialog open={Boolean(selectedRemitRef)}>
                <DialogContent
                    variant="drawer"
                    title={`Match claim for ${
                        selectedRemitGroup?.patientName || "payment"
                    }`}
                    handleCloseDialog={handleCloseClaimPicker}
                    wrapperClass="w-[760px]"
                    showFooter={false}
                >
                    <>
                        {selectedRemitGroup && (
                            <div className="mb-[--spacing-header] grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-[--spacing-items] bg-[--card-bg-gray] rounded-md border border-strokedark px-[--padding-card-x] py-[--padding-card-y]">
                                <div>
                                    <p className="section-info">Patient</p>
                                    <p className="section-value">
                                        {selectedRemitGroup.patientName || "--"}
                                    </p>
                                </div>
                                <div>
                                    <p className="section-info">CPT code</p>
                                    <p className="section-value">
                                        {selectedRemitGroup.cptCodes.join(
                                            ", "
                                        ) || "--"}
                                    </p>
                                </div>
                                <div>
                                    <p className="section-info">Amount paid</p>
                                    <p className="section-value">
                                        {showMoneyInAppFormat(
                                            selectedRemitGroup.paidAmount
                                        )}
                                    </p>
                                </div>
                                <div>
                                    <p className="section-info">
                                        Remit reference
                                    </p>
                                    <p className="section-value">
                                        {selectedRemitGroup.ref}
                                    </p>
                                </div>
                            </div>
                        )}
                        <SearchInput
                            placeholder="Search by client name or claim ID"
                            containerClass="mb-[--spacing-items]"
                            value={searchString}
                            onChange={(e) => setSearchString(e.target.value)}
                        />
                        <div className="flex items-center gap-[--spacing-items] mb-[--spacing-items]">
                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton>Bill Status</FilterButton>
                                </DropdownTrigger>
                                <DropdownContent align="start" width="auto">
                                    {Object.values(BillStatus).map((status) => (
                                        <DropdownItem
                                            key={status}
                                            onClick={() =>
                                                handleBillStatusChange(status)
                                            }
                                            isMulti
                                            itemValue={status}
                                            values={billStatusFilter}
                                        >
                                            {getBillStatusFilterLabel(status)}
                                        </DropdownItem>
                                    ))}
                                </DropdownContent>
                            </Dropdown>
                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton>Claim Status</FilterButton>
                                </DropdownTrigger>
                                <DropdownContent align="start" width="auto">
                                    {Object.values(ClaimStatus).map(
                                        (status) => (
                                            <DropdownItem
                                                key={status}
                                                onClick={() =>
                                                    handleClaimStatusChange(
                                                        status
                                                    )
                                                }
                                                isMulti
                                                itemValue={status}
                                                values={claimStatusFilter}
                                            >
                                                {getClaimStatusFilterLabel(
                                                    status
                                                )}
                                            </DropdownItem>
                                        )
                                    )}
                                </DropdownContent>
                            </Dropdown>
                        </div>
                        <TableFiltersDisplay
                            appliedFilters={[
                                {
                                    key: "invoice_statuses[]",
                                    label: "Bill Status",
                                    values: billStatusFilter.map(
                                        (s) => BillStatusLabels[s] || s
                                    ),
                                    preserveValues: true,
                                },
                                {
                                    key: "claim_statuses[]",
                                    label: "Claim Status",
                                    values: claimStatusFilter,
                                },
                            ]}
                            wrapperClass="mb-[--spacing-header]"
                        />
                        {error && error?.response?.status !== 404 && (
                            <ListState
                                isError
                                stateHelperText="Try reloading this page to solve this issue"
                                errorMsg="Cannot display claims at this time please try again later"
                            />
                        )}
                        {isFetching && <Loader />}
                        {!isFetching &&
                            isSuccess &&
                            rankedClaims.length === 0 && (
                                <ListState
                                    stateHelperText="Try a different name or claim ID"
                                    isError={false}
                                    emptyMessage="No claims found for this search"
                                />
                            )}
                        {!isFetching &&
                            isSuccess &&
                            rankedClaims.length > 0 && (
                                <Table
                                    columns={claimColumns}
                                    data={rankedClaims}
                                />
                            )}
                        {!searchString && !isFetching && (
                            <p className="text-sm text-center text-textSec my-[40px]">
                                Search for the claim this payment belongs to
                            </p>
                        )}
                    </>
                </DialogContent>
            </Dialog>

            {remitGroups.length === 0 ? (
                <ListState
                    stateHelperText="Unmatched payments will appear here"
                    isError={false}
                    emptyMessage="There are no unmatched payments on this remit"
                />
            ) : (
                <>
                    <p className="text-sm text-textSec mb-[--spacing-items]">
                        Select a payment to match it to a claim
                    </p>
                    <Table
                        columns={unmatchedColumns}
                        data={remitGroups}
                        handleRowClick={handleOpenClaimPicker}
                    />
                </>
            )}
        </>
    );
}
